import { useSyncExternalStore } from "react";

/**
 * Sound effects (SPEC 9.6, T4.7a): Kenney CC0 clips in src/audio/sfx (built by scripts/fetch-sfx.py,
 * already loudness-matched), played through Web Audio.
 * - Nothing plays before the first tap or key press (browsers block it; SPEC 9.6).
 * - iOS: the audio session is "ambient", so the ring/silent switch mutes the effects.
 * - On/off: the parent sets a default per child (Khu phụ huynh); the child's speaker button
 *   overrides it on this device until the parent changes the default again (owner, 2026-10-20).
 */
export const SFX = [
  "correct",
  "retry",
  "star",
  "coin",
  "done0",
  "done1",
  "done2",
  "done3",
  "levelup",
  "badge",
  "gamewin",
] as const;
export type Sfx = (typeof SFX)[number];

/** the child's own choice, remembered with the parent default it was made against */
export interface SoundPref {
  on: boolean;
  base: boolean;
}

export function effectiveSound(pref: SoundPref | null, parentDefault: boolean): boolean {
  return pref && pref.base === parentDefault ? pref.on : parentDefault;
}

export interface SoundDeps {
  createContext: () => AudioContext | null;
  fetchClip: (name: Sfx) => Promise<ArrayBuffer>;
  storage: () => Storage | null;
  hidden: () => boolean;
}

const prefKey = (childId: string) => `mb-sound-${childId}`;

export class SoundManager {
  private ctx: AudioContext | null = null;
  private out: GainNode | null = null;
  private readonly clips = new Map<Sfx, AudioBuffer>();
  private loading: Promise<void> | null = null;
  private child: { id: string; parentDefault: boolean } | null = null;
  private on = false;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly deps: SoundDeps) {}

  get enabled(): boolean {
    return this.on;
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  /** the active child and the parent's default for it (null: no child, no sound) */
  configure(childId: string | null, parentDefault: boolean): void {
    this.child = childId ? { id: childId, parentDefault } : null;
    this.set(childId ? effectiveSound(this.readPref(childId), parentDefault) : false);
  }

  /** the child's speaker button */
  toggle(): void {
    if (!this.child) return;
    const on = !this.on;
    try {
      this.deps
        .storage()
        ?.setItem(prefKey(this.child.id), JSON.stringify({ on, base: this.child.parentDefault }));
    } catch {
      /* storage blocked: the choice lasts until the page reloads */
    }
    this.set(on);
    // a tap on the button is a user gesture: the confirmation can play right away
    if (on) this.play("correct");
  }

  /** first user gesture (and every later one, to resume after iOS suspends the context) */
  unlock(): void {
    if (!this.ctx) {
      this.ctx = this.deps.createContext();
      if (!this.ctx) return;
      this.out = this.ctx.createGain();
      this.out.gain.value = 0.9;
      this.out.connect(this.ctx.destination);
    }
    if (this.ctx.state !== "running") void this.ctx.resume().catch(() => undefined);
    if (this.on) void this.load();
  }

  /** play one clip now (or `delay` seconds later); `rate` > 1 sounds higher */
  play(name: Sfx, { delay = 0, rate = 1 }: { delay?: number; rate?: number } = {}): void {
    const { ctx, out } = this;
    if (!this.on || !ctx || !out || ctx.state !== "running" || this.deps.hidden()) return;
    const clip = this.clips.get(name);
    if (!clip) return;
    const src = ctx.createBufferSource();
    src.buffer = clip;
    src.playbackRate.value = rate;
    src.connect(out);
    src.start(ctx.currentTime + delay);
  }

  /** decode every clip once (about 70 KB); a failed fetch is retried at the next gesture */
  private load(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return Promise.resolve();
    this.loading ??= Promise.all(
      SFX.filter((n) => !this.clips.has(n)).map(async (n) => {
        try {
          this.clips.set(n, await ctx.decodeAudioData(await this.deps.fetchClip(n)));
        } catch {
          /* offline or unsupported: stay silent */
        }
      }),
    ).then(() => {
      if (this.clips.size < SFX.length) this.loading = null;
    });
    return this.loading;
  }

  private readPref(childId: string): SoundPref | null {
    try {
      const raw = this.deps.storage()?.getItem(prefKey(childId));
      const p = raw ? (JSON.parse(raw) as Partial<SoundPref>) : null;
      return p && typeof p.on === "boolean" && typeof p.base === "boolean"
        ? { on: p.on, base: p.base }
        : null;
    } catch {
      return null;
    }
  }

  private set(on: boolean): void {
    if (on === this.on) return;
    this.on = on;
    if (on && this.ctx) void this.load();
    this.listeners.forEach((fn) => fn());
  }
}

function browserContext(): AudioContext | null {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const AC = w.AudioContext ?? w.webkitAudioContext;
  if (!AC) return null;
  try {
    // Safari 16.4+: respect the silent switch and mix with other apps' audio
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession) nav.audioSession.type = "ambient";
  } catch {
    /* older browsers */
  }
  try {
    return new AC();
  } catch {
    return null;
  }
}

/**
 * The clips' URLs (T4.9b): built into /assets/ with a content hash, so a re-cut clip reaches every
 * device at once although /assets/ is cached for a year.
 */
const CLIPS: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("../audio/sfx/*.mp3", {
      query: "?url",
      import: "default",
      eager: true,
    }),
  ).map(([path, url]) => [path.slice(path.lastIndexOf("/") + 1, -".mp3".length), url]),
);

export function clipUrl(name: Sfx): string | null {
  return CLIPS[name] ?? null;
}

export const sound = new SoundManager({
  createContext: browserContext,
  fetchClip: async (name) => {
    const url = clipUrl(name);
    if (!url) throw new Error(`sfx ${name}: no clip`);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`sfx ${name}: ${res.status}`);
    return res.arrayBuffer();
  },
  storage: () => {
    try {
      return localStorage;
    } catch {
      return null;
    }
  },
  hidden: () => typeof document !== "undefined" && document.hidden,
});

/** call once at start-up: audio may only start after a user gesture */
export function installSoundUnlock(): void {
  const unlock = () => sound.unlock();
  window.addEventListener("pointerdown", unlock, { capture: true, passive: true });
  window.addEventListener("keydown", unlock, { capture: true });
}

export function useSoundOn(): boolean {
  return useSyncExternalStore(sound.subscribe, () => sound.enabled);
}

/** end of a mini-game: a victory tune when the child wins, a friendly one otherwise */
export function playGameOver(winner: 0 | 1 | null): void {
  sound.play(winner === 0 ? "gamewin" : winner === null ? "done1" : "done0");
}

/** when star i pops in on the results screen, in seconds (the Stars animation uses the same) */
export const starDelay = (i: number) => 0.2 + 0.3 * i;

/**
 * A finished session: a "ting" per star as it pops in (each a little higher), a jingle by stars,
 * then coins. Returns when it is over (seconds), so a level-up or badge can follow.
 */
export function playSessionDone(stars: number, coins: number, jingle?: Sfx): number {
  const s = Math.max(0, Math.min(3, Math.round(stars)));
  for (let i = 0; i < s; i++) sound.play("star", { delay: starDelay(i), rate: 1 + 0.12 * i });
  const at = s ? starDelay(s) : 0.1;
  sound.play(jingle ?? (`done${s}` as Sfx), { delay: at });
  if (coins > 0) sound.play("coin", { delay: at + 0.7 });
  return at + 1.3;
}
